// Typed client for the conversations persistence API. Each call acquires a
// short-lived access token for the app's own API scope (silently, using the
// existing MSAL session) and attaches it as a bearer token.
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { apiRequest } from '@/config/authConfig'
import { appConfig } from '@/config/appConfig'
import type {
  ConversationDetail,
  ConversationInput,
  ConversationSummary,
} from '@/types/conversation'

async function acquireApiToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<string> {
  try {
    const result = await instance.acquireTokenSilent({ ...apiRequest, account })
    return result.accessToken
  } catch (error) {
    // If consent/interaction is required, fall back to a redirect (the page
    // reloads and resumes once the token is granted).
    if (error instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect(apiRequest)
    }
    throw error
  }
}

async function apiFetch<T>(
  instance: IPublicClientApplication,
  account: AccountInfo,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const token = await acquireApiToken(instance, account)
  const response = await fetch(`${appConfig.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed with ${response.status}`)
  }
  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

/**
 * Reports whether server-side persistence is available. Returns false on any
 * failure (backend not running, Cosmos/auth not configured) so the app can
 * degrade to a single in-memory conversation.
 */
export async function checkPersistence(): Promise<boolean> {
  try {
    const response = await fetch(`${appConfig.apiBaseUrl}/api/health`)
    if (!response.ok) return false
    const data = (await response.json()) as { cosmos?: boolean; auth?: boolean }
    return Boolean(data.cosmos && data.auth) && apiRequest.scopes.length > 0
  } catch {
    return false
  }
}

export function listConversations(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<ConversationSummary[]> {
  return apiFetch(instance, account, '/api/conversations')
}

export function createConversation(
  instance: IPublicClientApplication,
  account: AccountInfo,
  body: ConversationInput,
): Promise<ConversationDetail> {
  return apiFetch(instance, account, '/api/conversations', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function getConversation(
  instance: IPublicClientApplication,
  account: AccountInfo,
  id: string,
): Promise<ConversationDetail> {
  return apiFetch(instance, account, `/api/conversations/${id}`)
}

export function saveConversation(
  instance: IPublicClientApplication,
  account: AccountInfo,
  id: string,
  body: ConversationInput,
): Promise<ConversationDetail> {
  return apiFetch(instance, account, `/api/conversations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function deleteConversation(
  instance: IPublicClientApplication,
  account: AccountInfo,
  id: string,
): Promise<void> {
  return apiFetch(instance, account, `/api/conversations/${id}`, { method: 'DELETE' })
}
